/**
 * Team stats tab — ranked bars derived from the standings we already have
 * (goals scored / conceded / goal difference). Possession & shots from the design
 * aren't in our data, so they're intentionally omitted rather than faked.
 */

import React, { useMemo, useState } from 'react'
import { View, Text } from 'react-native'
import { useTheme } from '../../../src/theme/useTheme'
import { SoccerKit } from '../../ui/SoccerKit/SoccerKit'
import { CountryFlag } from '../../worldcup/CountryFlag/CountryFlag'
import { getTeamKitConfigFromTeam } from '../../../helpers/teamKit.helper'
import { AnimatedSegmentedControl } from '../../ui/AnimatedSegmentedControl'
import { METRIC_META, METRIC_OPTIONS } from '../leagueDetail.constants'
import type { NormalizedStandings, StandingRow } from '../../../src/leagueDetail'
import {
  dedupeRows,
  getAccentColorStyle,
  getBarWidthStyle,
  metricValue,
  type TeamMetricKey,
} from '../../../src/leagueDetail/helpers/leagueTeamStats.helper'
import { getLeagueTeamStatsStyles } from './LeagueTeamStats.styles'

const MAX_ROWS = 10

interface RankedTeam {
  row: StandingRow
  value: number
}

interface LeagueTeamStatsProps {
  groups: NormalizedStandings['groups']
  accent?: string
}

export const LeagueTeamStats: React.FC<LeagueTeamStatsProps> = ({ groups, accent }) => {
  const theme = useTheme()
  const styles = useMemo(() => getLeagueTeamStatsStyles(theme), [theme])
  const [metric, setMetric] = useState<TeamMetricKey>('scored')

  const ranked = useMemo<RankedTeam[]>(() => {
    const rows = dedupeRows(groups)
    const mapped = rows.map((row) => ({ row, value: metricValue(row, metric) }))
    mapped.sort((a, b) => (METRIC_META[metric].fewestFirst ? a.value - b.value : b.value - a.value))
    return mapped.slice(0, MAX_ROWS)
  }, [groups, metric])

  const maxMagnitude = useMemo(
    () => Math.max(1, ...ranked.map((t) => Math.abs(t.value))),
    [ranked],
  )

  const meta = METRIC_META[metric]
  const accentColor = accent ?? theme.primary

  return (
    <View style={styles.root}>
      <AnimatedSegmentedControl<TeamMetricKey>
        variant="sportsBar"
        options={METRIC_OPTIONS}
        value={metric}
        onChange={setMetric}
        testID="league-team-stats-metric"
      />

      <View>
        <View style={styles.sectionLabelRow}>
          <Text style={styles.sectionLabel}>{meta.label}</Text>
          <Text style={styles.sectionHint}>{meta.hint}</Text>
        </View>

        {ranked.length === 0 ? (
          <Text style={styles.empty}>Team stats will appear once standings are available.</Text>
        ) : (
          <View style={styles.card}>
            {ranked.map((t, index) => {
              const leader = index === 0
              const kit = getTeamKitConfigFromTeam({
                name: t.row.displayName,
                kitPrimary: t.row.kitPrimary ?? null,
                kitSecondary: t.row.kitSecondary ?? null,
                kitType: t.row.kitType ?? null,
              })
              const pct = Math.max(6, (Math.abs(t.value) / maxMagnitude) * 100)
              const valueLabel = metric === 'diff' && t.value > 0 ? `+${t.value}` : `${t.value}`
              return (
                <View key={t.row.teamId} style={styles.row}>
                  <Text style={[styles.rank, leader && styles.rankLeader]}>{index + 1}</Text>
                  {t.row.countryCode != null && t.row.countryCode !== '' ? (
                    <CountryFlag
                      countryCode={t.row.countryCode}
                      kitFallback={kit}
                      size="mini"
                      accessibilityLabel={t.row.displayName}
                    />
                  ) : (
                    <SoccerKit primary={kit.primary} secondary={kit.secondary} type={kit.type} size={22} />
                  )}
                  <View style={styles.body}>
                    <View style={styles.nameRow}>
                      <Text style={styles.name} numberOfLines={1}>{t.row.displayName}</Text>
                      <Text style={styles.value}>{valueLabel}</Text>
                    </View>
                    <View style={styles.track}>
                      <View
                        style={[
                          styles.fill,
                          getBarWidthStyle(pct),
                          leader ? styles.leaderFill : getAccentColorStyle(accentColor),
                        ]}
                      />
                    </View>
                  </View>
                </View>
              )
            })}
          </View>
        )}
      </View>
    </View>
  )
}
